import React, { useState, useEffect, useCallback } from 'react';
import {
  Megaphone, Plus, Search, X, Pencil, Trash2, RefreshCw, Pin, Eye,
  Send, FileEdit, Archive, ChevronLeft, ChevronRight, Users
} from 'lucide-react';
import { LoadingSpinner, ErrorMessage, SuccessMessage } from '../../UI';
import { announcementAPI, clientAPI } from '../../../services';
import { useAuth } from '../../../context/AuthContext';
import { formatDate, toDateInput } from './billing/lineItems';
import { FONT_UI, PageHeader, PrimaryButton, GhostButton } from './theme';

const STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: FileEdit },
  { value: 'published', label: 'Published', color: 'bg-green-100 text-green-800', icon: Send },
  { value: 'archived', label: 'Archived', color: 'bg-gray-100 text-gray-500', icon: Archive }
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700' },
  { value: 'normal', label: 'Normal', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-amber-100 text-amber-700' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700' }
];

const AUDIENCES = [
  { value: 'all', label: 'Everyone' },
  { value: 'selected', label: 'Selected clients' },
  { value: 'admins', label: 'Client admins only' },
  { value: 'employees', label: 'Employees only' }
];

const statusMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];
const priorityMeta = (v) => PRIORITIES.find((p) => p.value === v) || PRIORITIES[1];
const audienceLabel = (v) => AUDIENCES.find((a) => a.value === v)?.label || v;

const emptyForm = () => ({
  title: '',
  body: '',
  audience: 'all',
  clients: [],
  priority: 'normal',
  status: 'draft',
  pinned: false,
  publishAt: new Date().toISOString().split('T')[0],
  expiresAt: ''
});

const AnnouncementsPage = () => {
  const { user } = useAuth();
  const canManage = (action) =>
    user?.role !== 'platform_staff' || user?.platformPermissions?.announcements?.[action] === true;

  const [announcements, setAnnouncements] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const [filters, setFilters] = useState({ search: '', status: '', priority: '' });
  const [page, setPage] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [viewing, setViewing] = useState(null);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: pagination.limit };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const response = await announcementAPI.getAnnouncements(params);
      setAnnouncements(response.data.announcements || []);
      setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
    } catch (err) {
      setError(err.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, [page, filters, pagination.limit]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  useEffect(() => {
    clientAPI
      .getClients()
      .then((res) => setClients(res.data.clients || []))
      .catch((err) => console.error('Failed to load clients:', err.message));
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setIsModalOpen(true);
  };

  const openEdit = (a) => {
    setEditingId(a._id);
    setForm({
      title: a.title || '',
      body: a.body || '',
      audience: a.audience || 'all',
      clients: (a.clients || []).map((c) => c._id || c),
      priority: a.priority || 'normal',
      status: a.status || 'draft',
      pinned: !!a.pinned,
      publishAt: toDateInput(a.publishAt),
      expiresAt: toDateInput(a.expiresAt)
    });
    setIsModalOpen(true);
  };

  const openDetail = async (a) => {
    try {
      // Read receipts aren't included in the list response.
      const response = await announcementAPI.getAnnouncement(a._id);
      setViewing(response.data);
    } catch (err) {
      setError(err.message || 'Failed to load announcement');
    }
  };

  const toggleClient = (id) => {
    setForm((f) => ({
      ...f,
      clients: f.clients.includes(id) ? f.clients.filter((c) => c !== id) : [...f.clients, id]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.audience === 'selected' && form.clients.length === 0) {
      setError('Select at least one client for a targeted announcement');
      return;
    }

    const payload = {
      title: form.title,
      body: form.body,
      audience: form.audience,
      clients: form.audience === 'selected' ? form.clients : [],
      priority: form.priority,
      status: form.status,
      pinned: form.pinned,
      publishAt: form.publishAt,
      expiresAt: form.expiresAt || null
    };

    try {
      setSaving(true);
      if (editingId) {
        await announcementAPI.updateAnnouncement(editingId, payload);
        setSuccess('Announcement updated');
      } else {
        await announcementAPI.createAnnouncement(payload);
        setSuccess('Announcement created');
      }
      setIsModalOpen(false);
      fetchAnnouncements();
    } catch (err) {
      setError(err.message || 'Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  // Publishing straight from the list is the common action, so it gets its own shortcut.
  const handlePublish = async (a) => {
    try {
      await announcementAPI.updateAnnouncement(a._id, { status: 'published', publishAt: new Date().toISOString() });
      setSuccess(`"${a.title}" published`);
      fetchAnnouncements();
    } catch (err) {
      setError(err.message || 'Failed to publish announcement');
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await announcementAPI.deleteAnnouncement(a._id);
      setSuccess('Announcement deleted');
      fetchAnnouncements();
    } catch (err) {
      setError(err.message || 'Failed to delete announcement');
    }
  };

  const counts = {
    total: pagination.total,
    published: announcements.filter((a) => a.status === 'published').length,
    draft: announcements.filter((a) => a.status === 'draft').length,
    pinned: announcements.filter((a) => a.pinned).length
  };

  return (
    <div style={{ fontFamily: FONT_UI }}>
      <PageHeader
        title="Announcements"
        subtitle="Broadcast platform notices to clients, admins and employees"
        actions={
          <>
            <GhostButton icon={RefreshCw} onClick={fetchAnnouncements}>Refresh</GhostButton>
            {canManage('create') && (
              <PrimaryButton icon={Plus} onClick={openCreate}>New announcement</PrimaryButton>
            )}
          </>
        }
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} className="mb-4" />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} className="mb-4" />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <Megaphone className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">{counts.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Send className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Published (page)</p>
              <p className="text-2xl font-bold text-green-600">{counts.published}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <FileEdit className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Drafts (page)</p>
              <p className="text-2xl font-bold text-amber-600">{counts.draft}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Pin className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pinned (page)</p>
              <p className="text-2xl font-bold text-blue-600">{counts.pinned}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 mb-4 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative sm:col-span-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, search: e.target.value });
              }}
              placeholder="Search title or message..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => {
              setPage(1);
              setFilters({ ...filters, status: e.target.value });
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <select
            value={filters.priority}
            onChange={(e) => {
              setPage(1);
              setFilters({ ...filters, priority: e.target.value });
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All priorities</option>
            {PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : announcements.length === 0 ? (
          <div className="text-center py-16">
            <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No announcements yet</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {announcements.map((a) => {
                const StatusIcon = statusMeta(a.status).icon;
                return (
                  <div key={a._id} className="p-4 sm:p-5 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          {a.pinned && <Pin className="w-4 h-4 text-blue-600 shrink-0" />}
                          <h3 className="text-sm font-semibold text-gray-900 truncate">{a.title}</h3>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusMeta(a.status).color}`}>
                            <StatusIcon className="w-3 h-3" /> {statusMeta(a.status).label}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${priorityMeta(a.priority).color}`}>
                            {priorityMeta(a.priority).label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{a.body}</p>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                          <span className="inline-flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            {audienceLabel(a.audience)}
                            {a.audience === 'selected' && ` (${a.clients?.length || 0})`}
                          </span>
                          <span>Publish {formatDate(a.publishAt)}</span>
                          {a.expiresAt && <span>Expires {formatDate(a.expiresAt)}</span>}
                          <span>{a.readCount || 0} reads</span>
                          {a.createdBy?.name && <span>by {a.createdBy.name}</span>}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => openDetail(a)} className="text-gray-400 hover:text-blue-600" title="View">
                          <Eye className="w-4 h-4" />
                        </button>
                        {canManage('update') && a.status === 'draft' && (
                          <button onClick={() => handlePublish(a)} className="text-gray-400 hover:text-green-600" title="Publish now">
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                        {canManage('update') && (
                          <button onClick={() => openEdit(a)} className="text-gray-400 hover:text-blue-600" title="Edit">
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {canManage('delete') && (
                          <button onClick={() => handleDelete(a)} className="text-gray-400 hover:text-red-600" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages} &middot; {pagination.total} announcements
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.page <= 1}
                    className="p-1.5 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                    disabled={pagination.page >= pagination.pages}
                    className="p-1.5 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Composer */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Edit Announcement' : 'New Announcement'}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Scheduled maintenance this Sunday"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  required
                  rows={6}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Audience</label>
                  <select
                    value={form.audience}
                    onChange={(e) => setForm({ ...form, audience: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {AUDIENCES.map((a) => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Client picker only matters for a targeted announcement */}
              {form.audience === 'selected' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Clients ({form.clients.length} selected)
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {clients.length === 0 && <p className="p-3 text-sm text-gray-500">No clients available</p>}
                    {clients.map((c) => (
                      <label key={c._id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.clients.includes(c._id)}
                          onChange={() => toggleClient(c._id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{c.name} <span className="text-gray-400">({c.code})</span></span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Publish On</label>
                  <input
                    type="date"
                    value={form.publishAt}
                    onChange={(e) => setForm({ ...form, publishAt: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expires On</label>
                  <input
                    type="date"
                    value={form.expiresAt}
                    onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.pinned}
                  onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Pin to the top of the feed</span>
              </label>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Announcement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail with read receipts */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-gray-900">{viewing.title}</h2>
              <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusMeta(viewing.status).color}`}>
                  {statusMeta(viewing.status).label}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityMeta(viewing.priority).color}`}>
                  {priorityMeta(viewing.priority).label}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  {audienceLabel(viewing.audience)}
                </span>
              </div>

              <p className="text-sm text-gray-700 whitespace-pre-wrap">{viewing.body}</p>

              {viewing.audience === 'selected' && (viewing.clients || []).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Target Clients</p>
                  <p className="text-sm text-gray-600">{viewing.clients.map((c) => c.name).join(', ')}</p>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm pt-2 border-t border-gray-200">
                <div>
                  <p className="text-gray-500">Publish</p>
                  <p className="font-medium text-gray-900">{formatDate(viewing.publishAt)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Expires</p>
                  <p className="font-medium text-gray-900">{formatDate(viewing.expiresAt)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Reads</p>
                  <p className="font-medium text-gray-900">{viewing.readBy?.length || 0}</p>
                </div>
              </div>

              {(viewing.readBy || []).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Read By</p>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {viewing.readBy.map((r, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-gray-700">{r.user?.name || 'Unknown user'}</span>
                        <span className="text-gray-500 text-xs">{formatDate(r.readAt)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsPage;
